import { useCallback, useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import InfiniteScroll from "react-infinite-scroller";
import { useSelector } from "react-redux";
import { Box } from "@mui/material";
import {
  callAPIInterface,
  handleOpenAppDetailDialogAndPassDetails,
  removedAppStatus,
} from "../../constants";
import List from "../List";
import CommonHeader from "../../CommonHeader";
import {
  action,
  myAppsLabel,
  remove,
  selectActionTxt,
  viewDetails,
} from "../../messages";
import CommonSkeleton from "../../CommonSkeleton";
import CommonDrawer from "../../drawer/CommonDrawer";
import Text from "../../text/Text";
import WalletSymbols from "../../WalletSymbols";
import InstalledAppDialog from "../InstalledAppDialog";
import AppDetailsDialog from "../AppDetailsDialog";
import RemoveAppConfirmationModal from "./RemoveAppConfirmationModal";

function MyAppsList() {
  const queryParam = "?limit=50";

  const [myApps, setMyApps] = useState([]);
  const [queryParams, setQueryParams] = useState(queryParam);
  const [hasMore, setHasMore] = useState(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isDeleteLoader, setIsDeleteLoader] = useState(false);

  const { isOpenInstalledAppDialog, isAppDetailOpen } = useSelector(
    (state) => state.common
  );

  useEffect(() => {
    handleGetMyApps([], queryParams);
  }, []);

  const handleGetMyApps = async (lines, queryParams) => {
    try {
      const myApps = await callAPIInterface(
        "GET",
        `/manage-app/apps-account${queryParams}`
      );

      if (!myApps?.has_more) {
        setHasMore(false);
      } else {
        setQueryParams(
          queryParam + "&ending_before=" + myApps?.pagination_token
        );
      }
      setMyApps(lines.concat(myApps?.data));
    } catch (err) {
      console.log("error while calling app-account", err);
    }
  };

  const loadMore = useCallback(() => {
    if (hasMore) {
      handleGetMyApps(myApps, queryParams);
    }
  }, [myApps, queryParams, hasMore]);

  const listSkeleton = () => {
    return (
      <Box className="list-box">
        <Box display="flex" gap="10px">
          <CommonSkeleton
            variant="rectangular"
            height="56px"
            width="56px"
            borderRadius="14px"
          />
          <Box>
            <CommonSkeleton variant="text" />
            <CommonSkeleton variant="text" width="200px" />
          </Box>
        </Box>
        <CommonSkeleton
          variant="square"
          height="33px"
          width="70px"
          borderRadius="8px"
        />
      </Box>
    );
  };

  const handleRemoveMyApp = async () => {
    try {
      setIsDeleteLoader(true);
      await callAPIInterface(
        "DELETE",
        `/manage-app/apps-account/${selectedId}`
      );

      const updated = myApps.filter((app) => app.app_id.id !== selectedId);

      setMyApps(updated);
      setIsConfirmModalOpen(false);
    } catch (err) {
      console.log("Error while removing my app", err);
    } finally {
      setIsDeleteLoader(false);
    }
  };

  const actions = [
    {
      label: viewDetails,
      className: "default-text",
      onClick: () => {
        handleOpenAppDetailDialogAndPassDetails({ id: selectedId });
        setIsDrawerOpen(false);
      },
    },
    {
      label: remove,
      className: "error-text",
      onClick: () => {
        setIsDrawerOpen(false);
        setIsConfirmModalOpen(true);
      },
    },
  ];

  return (
    <>
      <CommonHeader icon="backArrow" textAfterIcon={myAppsLabel} />
      <Box className="options-container">
        <Box className="my-app-wrapper">
          <InfiniteScroll
            pageStart={0}
            loadMore={loadMore}
            initialLoad={false}
            hasMore={hasMore}
            loader={listSkeleton()}
            useWindow={false}
            className="transaction-list"
            style={{ width: "100%" }}
          >
            {myApps?.map((data, idx) => {
              return (
                <List
                  key={data.id || idx}
                  data={{ ...data?.app_id }}
                  idx={idx}
                  dataLength={myApps.length}
                  setIsDrawerOpen={setIsDrawerOpen}
                  setSelectedId={setSelectedId}
                  isFrom="myApps"
                  isMainListPage={false}
                  isDisabled={removedAppStatus.includes(data?.app_id?.status)}
                />
              );
            })}
          </InfiniteScroll>

          {isDrawerOpen && (
            <CommonDrawer
              anchor="bottom"
              open={isDrawerOpen}
              onClose={() => setIsDrawerOpen(false)}
            >
              <Box padding="24px 16px">
                <Box className="drawer-header flex-between">
                  <Text
                    className="default-text"
                    size={20}
                    font="bold"
                    mb="20px"
                  >
                    {action}
                  </Text>
                  <CloseIcon
                    className="my-app-close-icon"
                    onClick={() => setIsDrawerOpen(false)}
                  />
                </Box>
                <Text size={14} font="regular" mb={2} className="grey-text">
                  {selectActionTxt}
                </Text>
                <Box display="flex" flexDirection="column" gap="16px">
                  {actions.map((act) => {
                    return (
                      <Box
                        key={act.label}
                        className="mini-app-drawer-action-btn"
                        onClick={act?.onClick}
                      >
                        <Text
                          size={16}
                          font="semibold"
                          className={act.className}
                        >
                          {act.label}
                        </Text>
                        <WalletSymbols
                          symbol="chevron_right"
                          sx={{ color: "#848B9E" }}
                        />
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </CommonDrawer>
          )}

          {isConfirmModalOpen && (
            <RemoveAppConfirmationModal
              handleAction={handleRemoveMyApp}
              isConfirmModalOpen={isConfirmModalOpen}
              isDeleteLoader={isDeleteLoader}
              setIsConfirmModalOpen={setIsConfirmModalOpen}
            />
          )}
        </Box>
      </Box>
      {/*Installed app and app details dialogs */}
      {isAppDetailOpen && <AppDetailsDialog />}
      {isOpenInstalledAppDialog && <InstalledAppDialog />}
    </>
  );
}

export default MyAppsList;
